import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatTooltip } from "@angular/material/tooltip";
import {
	LocalStorageKeys, SortableDirective,
	SortOutput
} from "@shared-utils";
import { NgUiSrefDirective } from "@shims";
import { Visits } from "@types-lib";
import { TableComponent } from "@ui-lib/components";
import { FullnamePipe } from "@ui-lib/pipes";
import { VisitOrderStore } from "@visits-store";
import { HumanisedStatusPipe } from './../../../../core/pipes/humanitised-status/humanised-status.pipe';

type VisitOrderSortableFields = 'paid' | 'order_id';

interface VisitOrderHeader {
	tooltip?: string;
	id: number,
	text: string;
	sortValue?: VisitOrderSortableFields;
	sortDirection?: 'up' | 'down'
}

@Component({
    selector: 'waiting-room-table',
    imports: [CommonModule, MatTooltip, NgUiSrefDirective, FullnamePipe, HumanisedStatusPipe, TableComponent, SortableDirective],
    templateUrl: './waiting-room-table.component.html',
    styleUrl: './waiting-room-table.component.scss'
})
export class WaitingRoomTableComponent{
	readonly visitOrdersStore = inject(VisitOrderStore)
	readonly noDataMessage = 'No visits to show'
	currentPagination: Visits.BasePagination = {
		limit: 100,
		offset: 0,
		total: 9999,
		saveOnChange: true
	}
	sortableFields: VisitOrderSortableFields[] = ['paid', 'order_id'];
	headers: VisitOrderHeader[] = [
		{
			id: 1,
			tooltip: 'Questionnaire Status',
			text: 'Q',
		},
		{
			id: 2,
			tooltip: 'Has Previously Cancelled Orders',
			text: 'C'
		},
		{
			id: 3,
			tooltip: 'Flagged Visit',
			text: 'F'
		},
		{
			id: 4,
			text: 'Paid At',
			sortValue: 'paid',
		},
		{
			id: 5,
			text: 'Name'
		},
		{
			id: 6,
			text: 'Visit Id',
			sortValue: 'order_id',
		},
		{
			id: 7,
			text: 'Service'
		},
		{
			id: 8,
			text: 'Medication'
		},
		{
			id: 9,
			text: 'Wait'
		},
		{
			id: 10,
			text: 'Status'
		},
		{
			id: 11,
			text: 'Last time called'
		},
		{
			id: 12,
			text: ''
		}
	]
	itemsPerPageOptions: string|number[] = [10, 20, 50, 75, 100]
	sortOrder: 'asc' | 'desc' = 'desc'
	protected readonly savePageLocalStorageKey:LocalStorageKeys = LocalStorageKeys.SAVE_VISIT_PAGE_KEY

	onSort(event: SortOutput<VisitOrderSortableFields>) {
		if (event) {
			let { sortField, sortOrder } = event;
			const headerToUpdate = this.headers.find(header => header.sortValue === sortField);
			sortOrder = sortField === this.visitOrdersStore.searchQuery().sort ? sortOrder : 'desc'
			if (headerToUpdate) {
				headerToUpdate.sortDirection = sortOrder === 'asc' ? 'up' : 'down';
			}
			this.headers.forEach(header => {
				if (header.sortValue !== sortField) {
					header.sortDirection = undefined;
				}
			});
			const query = { ...this.visitOrdersStore.searchQuery(), sort: sortField, order: sortOrder };
			this.visitOrdersStore.updateSearchQuery(query);
		}
	}


	onPaginationChange(event:Visits.BasePagination | undefined) {
		if(event) {
			const previousValues: Visits.BasePagination = (({ limit, offset, total }) => ({ limit, offset, total }))(event);
			const hasChanges = Object.keys(previousValues).some((key) => previousValues[key as keyof Visits.BasePagination] !== this.currentPagination[key as keyof Visits.BasePagination]);
			if (hasChanges) {
				this.currentPagination = {...event}
				const query = {...this.visitOrdersStore.searchQuery(),...event}
				this.visitOrdersStore.updateSearchQuery(query);
			}
		}
	}
}
